import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface StatProps {
  label: ReactNode;
  value: ReactNode;
  /** Short context rendered inline after the value: trend, comparison, unit. */
  sub?: ReactNode;
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
  className?: string;
}

const VALUE_TONE = {
  neutral: 'text-zinc-50',
  accent: 'text-accent-bright',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
} as const;

/**
 * One KPI cell. Never bordered on its own — render sets inside StatGroup
 * (one quiet divided strip), never a grid of boxes.
 */
export function Stat({ label, value, sub, tone = 'neutral', className }: StatProps) {
  return (
    <div className={cn('min-w-0 px-4 py-2.5', className)}>
      <div className="truncate text-xs font-medium text-zinc-400">{label}</div>
      <div className={cn('text-lg leading-6 font-semibold tabular-nums', VALUE_TONE[tone])}>
        {value}
        {sub ? <span className="ml-1.5 text-xs font-normal text-zinc-400">{sub}</span> : null}
      </div>
    </div>
  );
}

/** The one KPI container: a single bordered strip with divided Stat cells. */
export function StatGroup({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'corner-brand flex flex-wrap items-stretch divide-x divide-zinc-800 overflow-hidden border border-zinc-800 bg-zinc-900',
        '[&>*]:flex-1',
        className
      )}
    >
      {children}
    </div>
  );
}
